import { useState, useMemo, useCallback } from 'react';
import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';

import { formatDate, dateFormatForAPI } from 'utils/date';
import { BookingState } from 'constants/bookings';

export type StatementState =
  | 'void'
  | 'draft'
  | 'billed'
  | 'part_paid'
  | 'fully_paid';
export type PaymentState = 'pending' | 'paid' | 'failed';

interface Space {
  id: number;
}

export interface BookingItem {
  space: Space;
  id: number;
  state: BookingState;
  startTime: string;
  endTime: string;
  totalCost: string;
  cancellable: boolean;
  cancellableErrors: string[];
  statementId: number;
  statementNumber: string;
  statementDueDate: string;
  statementState: StatementState;
  paymentState: PaymentState;
  recurring: boolean;
}

const fixBookingItem = (spaceId: number) => (item: BookingItem): BookingItem =>
  'space' in item ? item : { ...item, space: { id: spaceId } };

const getBookingItems = (apiRequest, dates) => async (
  spaceId: number
): BookingItem[] => {
  const {
    data: { bookingItems = [] },
  } = ((await apiRequest.get(`/v1/spaces/${spaceId}/booking_items`, {
    params: { dates },
  })) as unknown) as { data: { bookingItems: BookingItem[] } };

  return bookingItems.map(fixBookingItem(spaceId));
};

const useBookingItems = (
  dates: Date | Date[] = [],
  spaceIds: number[] = []
) => {
  const [bookingItems, setBookingItems] = useState<BookingItem[]>([]);

  const apiRequest = useApiRequest();
  const requestValid =
    dates &&
    (!(dates instanceof Array) || dates.length > 0) &&
    spaceIds.length > 0;

  const datesJson = useMemo(
    () =>
      JSON.stringify(
        dates instanceof Array
          ? dates.map(date => formatDate(date, dateFormatForAPI))
          : [formatDate(dates, dateFormatForAPI)]
      ),
    [dates]
  );
  const spaceIdsJson = useMemo(() => JSON.stringify(spaceIds), [spaceIds]);

  const clearData = useCallback(async () => {
    setBookingItems([]);
  }, []);

  const fetchData = useCallback(async () => {
    const itemLists = await Promise.all(
      JSON.parse(spaceIdsJson).map(
        getBookingItems(apiRequest, JSON.parse(datesJson))
      )
    );

    setBookingItems(itemLists.flat());
  }, [apiRequest, datesJson, spaceIdsJson]);

  const { loading, error, refresh } = useAsync(
    requestValid ? fetchData : clearData,
    requestValid
  );

  return { data: bookingItems, loading, error, refresh };
};

export default useBookingItems;
